// Application constants and configuration values
// This file centralizes all magic numbers and configuration to improve maintainability

// File upload limits
const FILE_LIMITS = {
  MAX_FILE_SIZE: 20 * 1024 * 1024, // 20MB
  MAX_FILES_PER_REQUEST: 5,
  ALLOWED_MIME_TYPES: ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.png', '.jpg', '.jpeg', '.webp']
};

// Device detection breakpoints
const DEVICE_BREAKPOINTS = {
  MOBILE_MAX_WIDTH: 768,
  TABLET_MAX_WIDTH: 1024,
  DESKTOP_MIN_WIDTH: 1025,
  MOBILE_MAX_HEIGHT: 600,
  TABLET_MAX_HEIGHT: 800,
  DESKTOP_MIN_HEIGHT: 801
};

// Device types
const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

// Device names for AI prompts
const DEVICE_NAMES = {
  MOBILE: 'iPhone 17 Pro',
  TABLET: 'iPad Pro',
  DESKTOP: 'MacBook Pro',
  ULTRAWIDE: 'Apple Pro Display XDR'
};

// Credit costs for different operations
const CREDIT_COSTS = {
  ENHANCE_2K: 1,
  ENHANCE_4K: 2,
  GENERATE_MOCKUP: 1
};

// API timeouts (in milliseconds)
const TIMEOUTS = {
  FIRESTORE: 30000,
  API_REQUEST: 10000,
  IMAGE_PROCESSING: 60000,
  STRIPE_CHECKOUT: 15000
};

// Image dimensions for different resolutions
const IMAGE_DIMENSIONS = {
  ENHANCE_2K: { width: 2048, height: 1536 },
  ENHANCE_4K: { width: 3840, height: 2880 },
  OUTPUT_4_3: { width: 1600, height: 1200 }
};

// Rate limiting configuration
const RATE_LIMITS = {
  AI_OPERATIONS_PER_MINUTE: 10,
  UPLOAD_PER_MINUTE: 20,
  CHECKOUT_PER_MINUTE: 5,
  GENERAL_API_PER_MINUTE: 30
};

// Input validation limits
const INPUT_LIMITS = {
  FEEDBACK_MESSAGE_MAX_LENGTH: 5000,
  PROMPT_MAX_LENGTH: 1000,
  EMAIL_MAX_LENGTH: 254,
  NAME_MAX_LENGTH: 100,
  SUBJECT_MAX_LENGTH: 200
};

// Error messages (generic to prevent information leakage)
const ERROR_MESSAGES = {
  GENERIC_ERROR: 'An error occurred. Please try again.',
  UNAUTHORIZED: 'Unauthorized access.',
  VALIDATION_ERROR: 'Invalid input provided.',
  FILE_TOO_LARGE: 'File size exceeds limit.',
  INVALID_FILE_TYPE: 'Invalid file type.',
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please try again later.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  SERVER_ERROR: 'Server error. Please try again later.'
};

// Success messages
const SUCCESS_MESSAGES = {
  MOCKUP_GENERATED: 'Mockup generated successfully!',
  MOCKUP_ENHANCED: 'Mockup enhanced successfully!',
  FEEDBACK_SUBMITTED: 'Feedback submitted successfully!',
  PROFILE_UPDATED: 'Profile updated successfully!',
  CREDITS_ADDED: 'Credits added to your account!'
};

// Pipeline selection
const PIPELINES = {
  V1: 'v1',
  V3: 'v3'
};

// Model selection
const MODELS = {
  GEMINI_3_1_FLASH: {
    id: 'gemini-3.1-flash-image-preview',
    name: '3.1 Flash Image',
    fullName: 'Nano Banana 2 (Gemini 3.1 Flash Image)',
    bestFor: 'Pro capabilities at Flash speed — best of both worlds',
    credits: 1,
    apiType: 'google'
  },
  GEMINI_2_5_FLASH: {
    id: 'gemini-2.5-flash-image',
    name: '2.5 Flash Image',
    fullName: 'Nano Banana (Gemini 2.5 Flash Image)',
    bestFor: 'Speed and cost efficient — fallback option',
    credits: 1,
    apiType: 'google'
  }
};

// Helper function to get model credits
const getModelCredits = (modelId) => {
  const model = Object.values(MODELS).find(m => m.id === modelId);
  return model?.credits || 1;
};

// Helper function to get model display name
const getModelDisplayName = (modelId) => {
  const model = Object.values(MODELS).find(m => m.id === modelId);
  return model?.name || '3.1 Flash Image';
};

// API endpoints
const API_ENDPOINTS = {
  ENHANCE: '/api/enhance',    // Phase 1: Direct generation (default)
  ENHANCE_V1: '/api/enhance',    // Phase 1: Direct generation
  ENHANCE_V2: '/api/enhance-v2', // Phase 2: Two-step pipeline (not used)
  ENHANCE_V3: '/api/enhance-v3', // Phase 3: Mask-Based Inpainting (pixel-perfect fidelity)
  UPSCALE: '/api/upscale',
  HEALTH: '/api/health',
  CHECKOUT: '/.netlify/functions/create-checkout-session',
  FEEDBACK: '/api/send-feedback-email'
};

// Environment validation
const REQUIRED_ENV_VARS = {
  CLIENT: [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_STRIPE_PUBLISHABLE_KEY'
  ],
  SERVER: [
    'STRIPE_SECRET_KEY',
    'STRIPE_WEBHOOK_SECRET',
    'GEMINI_API_KEY',
    'REPLICATE_API_KEY',
    'FIREBASE_PROJECT_ID',
    'FIREBASE_PRIVATE_KEY',
    'FIREBASE_CLIENT_EMAIL'
  ]
};

// Default values for optional configuration
const DEFAULTS = {
  ASPECT_RATIO: '4:3',
  RESOLUTION: '2k',
  BILLING_PERIOD: 'one-time',
  DEVICE_TYPE: 'desktop',
  MOCKUP_COUNT: 1
};

// CommonJS exports for server and client compatibility
module.exports = {
  FILE_LIMITS,
  DEVICE_BREAKPOINTS,
  DEVICE_TYPES,
  DEVICE_NAMES,
  CREDIT_COSTS,
  TIMEOUTS,
  IMAGE_DIMENSIONS,
  RATE_LIMITS,
  INPUT_LIMITS,
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  PIPELINES,
  API_ENDPOINTS,
  REQUIRED_ENV_VARS,
  DEFAULTS,
  MODELS,
  getModelCredits,
  getModelDisplayName
};
